import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexTooltip, ApexXAxis, ApexYAxis } from "ngx-apexcharts";
import { BenchmarkDiagramPoint, BenchmarkDiagramSeries } from "../shared/benchmarks/benchmarks-model";
import { AltCardView, Card, FcstWalkCardType } from "../shared/project/cards/card";
import { CardsModel, Tab } from "../shared/project/cards/cards-model";
import { RoundingHelper, RoundingSettings } from "../shared/rounding/rounding-helper";
import { DimensionName } from "../shared/project/cards/dimension-name";
import { DimensionsFilter } from "../shared/project/dimensions-filtering/dimensions-filter";
import { LegendItem } from "../shared/project/cards/legend-item";
import { DimensionLockType } from "../shared/project/cards/dimension-lock-type";
import { FcstWalkTreeBuilder, MainFcstWalkModel } from "../shared/project/cards/fcst-walk-model";
import { BalanceCard } from "../shared/project/cards/balance-card";
import { Router, UrlTree } from "@angular/router";
import { DriverType } from "../shared/project/driver-types/driver-type";

export type ChartOptions = {
    labels: string[];
    chart: ApexChart;
    colors: string[];
    dataLabels: ApexDataLabels;
    tooltip: ApexTooltip;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    grid: any;
};

export class CardHelper {
    private static readonly DIAGRAM_COLOR: string = '#DE3F3F';

    public static createChartOptions(): ChartOptions {
        return {
            chart: {
                width: 120,
                height: 80,
                type: "area",
                toolbar: {
                    show: false
                },
                animations: {
                    enabled: false
                }
            },
            dataLabels: {
                enabled: false,
            },
            tooltip: {
                enabled: false
            },
            colors: [this.DIAGRAM_COLOR],
            labels: ["date1", "date2", "date3", "date4", "date5"],
            grid: {
                show: false
            },
            yaxis: {
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false
                },
                labels: {
                    show: false
                }
            },
            xaxis: {
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false
                },
                labels: {
                    show: false
                }
            }
        };
    }
    public static calcCardsFancyNumContexts(cards: Card[]) {
        for (let i = 0; i < cards.length; ++i) {
            if (cards[i].cardType === FcstWalkCardType.Precedent) {
                continue;
            }

            cards[i].outcomeCtx = this.createFancyNumCtxForNum((cards[i].altViewSeq.outcomeOldVal + cards[i].altViewSeq.outcomeNewVal) / 2);
            cards[i].formCompCtx = this.createFancyNumCtxForNum((cards[i].altViewSeq.formCompOldVal + cards[i].altViewSeq.formCompNewVal) / 2);
    
            let nums = [ cards[i].altViewSeq.formCompOldVal, cards[i].altViewSeq.formCompNewVal ];
            if (cards[i].selectedDiagram) {
                nums.push(cards[i].selectedDiagram.maxVal);
            }
            let previewDiagramRoundingSettings = RoundingHelper.CalcRoundingSettingsForDiagram(nums);
            cards[i].diagramMinMaxCtx = {
                roundTo: previewDiagramRoundingSettings.roundTo,
                scale: previewDiagramRoundingSettings.scale,
                use2stepRounding: false,
            };
            cards[i].diagramValsCtx = {
                roundTo: previewDiagramRoundingSettings.roundTo,
                scale: previewDiagramRoundingSettings.scale,
                use2stepRounding: true,
            };
        }
    }

    public static calcOutcomeFancyNumContext(cards: Card[], otherNums: number[]) {
        let minVal = Math.min.apply(null, otherNums.filter(n => n !== 0).map(n => Math.abs(n)));
        const eps = 0.0000000000001;
        for (let i = 0; i < cards.length; ++i) {
            if (Math.abs(cards[i].seqDriverValue) < minVal && Math.abs(cards[i].seqDriverValue) > eps) {
                minVal = Math.abs(cards[i].seqDriverValue);
            }
            if (Math.abs(cards[i].absDriverValue) < minVal && Math.abs(cards[i].absDriverValue) > eps) {
                minVal = Math.abs(cards[i].absDriverValue);
            }
        }
        return CardHelper.createFancyNumCtxForNum(minVal);
    }

    private static createFancyNumCtxForNum(num: number) {
        let roundingSettings = RoundingHelper.CalcRoundingSettingsForNum(num);
        return {
            roundTo: roundingSettings.roundTo,
            scale: roundingSettings.scale,
            use2stepRounding: true,
        };
    }

    public static initCardDiagram(card: Card, selectedDiagramSeriesName: string, oldControlVal: string, newControlVal: string, seqMode: boolean, viewBoxWidth:number, viewBoxHeight: number): void {
        card.selectedDiagram = (seqMode ? card.altViewSeq.diagramSeries : card.altViewSeq.diagramSeries).find(s => s.name === selectedDiagramSeriesName);

        let self = this;
        function initSeries(series: BenchmarkDiagramSeries[]) {
            let roundingSettings = self.CalcRoundingSettingsForDiagram(series);
            let oldControlValPt: BenchmarkDiagramPoint;
            let newControlValPt: BenchmarkDiagramPoint;
            for (let k = 0; k < series.length; ++k) {
                for (let m = 0; m < series[k].points.length; ++m) {
                    if (series[k].points[m].controlValue == oldControlVal) {
                        oldControlValPt = series[k].points[m];
                    }
                    if (series[k].points[m].controlValue == newControlVal) {
                        newControlValPt = series[k].points[m];
                    }
                }
            }
            for (let k = 0; k < series.length; ++k) {
                self.initDiagram(series[k], roundingSettings, oldControlValPt, newControlValPt, viewBoxWidth, viewBoxHeight);
            }
        }
        initSeries(card.altViewSeq.diagramSeries);
        this.initApexChart(card.altViewSeq, selectedDiagramSeriesName, oldControlVal, newControlVal);   //we no longer allow switching selected series, so only one diagram will be inited
        if (card.altViewTot && card.altViewTot.diagramSeries) {
            initSeries(card.altViewTot.diagramSeries);
            this.initApexChart(card.altViewTot, selectedDiagramSeriesName, oldControlVal, newControlVal);
        }
    }

    //whether a card shows the from-to of its formula component, "X to Y" or N/A for the period its rows have no line in (newLostType): its % (or Lost or New)
    //goes with it. A card without component values, e.g. a New, Lost or Maintain card with the outcome as the New/Lost source, has neither
    public static hasFormCompFromTo(altView: AltCardView): boolean {
        if (!altView) {
            return false;
        }
        switch (altView.newLostType) {
            case DriverType.lost:
                return altView.formCompOldVal != null;
            case DriverType.new:
                return altView.formCompNewVal != null;
            default:
                return altView.formCompOldVal != null && altView.formCompNewVal != null;
        }
    }

    public static initApexChart(altView: AltCardView, selectedDiagramSeriesName: string, oldControlVal: string, newControlVal: string) {
        let selectedSeries = altView.diagramSeries.find(s => s.name === selectedDiagramSeriesName);
        let series: ApexAxisChartSeries = [{
            name: selectedSeries.name,
            data: selectedSeries.points.map(point => ({
                x: point.xPos,
                y: point.value
            }))
        }];
        let oldVal = selectedSeries.points.find(p => p.controlValue == oldControlVal);
        let newVal = selectedSeries.points.find(p => p.controlValue == newControlVal);
        let color = selectedSeries.points[0].value > selectedSeries.points[selectedSeries.points.length - 1].value ? '#DE3F3F' : '#37D28A';
        altView.apexChartData = {
            fill: {
                colors: [color],
                type: 'gradient',
                gradient: {
                    type: 'vertical',
                    //shade: 'dark',
                    //shadeIntensity: 0.9,
                    gradientToColors: [color],
                    inverseColors: false,
                    opacityFrom: 0.9,
                    opacityTo: 0.3,
                },
            },
            annotations: (oldVal && newVal) ? {
                points: [{
                        x: oldVal.xPos,
                        y: oldVal.value,
                        marker: {
                            size: 4,
                            fillColor: color,
                            strokeColor: color,
                            //radius: 2,
                            shape: "circle",
                        }
                    },
                    {
                        x: newVal.xPos,
                        y: newVal.value,
                        marker: {
                            size: 4,
                            fillColor: color,
                            strokeColor: color,
                            //  radius: 2,
                            shape: "circle",
                        },
                    }
                ]
            } : null,
            stroke: {
                curve: "smooth",
                colors: [color],
            },
            series: series,
            colors: [color],
        };
    }

    private static CalcRoundingSettingsForDiagram(diagramSeries: BenchmarkDiagramSeries[]): RoundingSettings {
        let allNums: number[] = [];
        for (let i = 0; i < diagramSeries.length; ++i) {
            for (let j = 0; j < diagramSeries[i].points.length; ++j) {
                if (diagramSeries[i].points[j]) {
                    allNums.push(diagramSeries[i].points[j].value);
                }
            }
        }
        return RoundingHelper.CalcRoundingSettingsForDiagram(allNums);
    }
    
    private static initDiagram(diagramSeries: BenchmarkDiagramSeries, roundingSettings: RoundingSettings, oldControlValPoint: BenchmarkDiagramPoint, newControlValPoint: BenchmarkDiagramPoint, viewBoxWidth:number, viewBoxHeight: number) {
        if (!diagramSeries.points.length) {
            return;
        }

        let maxXPos = 0;
        let minXPos = 99;
        let xPoses =  diagramSeries.points.filter(p => p).map(p => p.xPos);
        if (oldControlValPoint) {
            xPoses.push(oldControlValPoint.xPos);
        }
        if (newControlValPoint) {
            xPoses.push(newControlValPoint.xPos);
        }
        for (let j = 0; j < xPoses.length; ++j) {
            let curXPos = xPoses[j];
            if (curXPos > maxXPos) {
                maxXPos = curXPos;
            }
            if (curXPos < minXPos) {
                minXPos = curXPos;
            }
        }
        let width = maxXPos - minXPos + 1;
        
        let minVal = roundingSettings.minAxis;
        let maxVal = roundingSettings.maxAxis;
        let xStep = viewBoxWidth / (width + 2);
        let verticalGap = 0;
        let yStep = (viewBoxHeight - 2 * verticalGap) / (maxVal - minVal);
        
        function convertX(x: number) {
            return xStep * x;
        }
        let self = this;
        function convertY(y: number) {
            return viewBoxHeight - verticalGap - yStep * (y - minVal);
        }

        for (let j = 0; j < diagramSeries.points.length; ++j) {
            let curPoint = diagramSeries.points[j];
            if (!curPoint) {
                continue;
            }
            curPoint.x = xStep * (curPoint.xPos - minXPos + 1.5);
            curPoint.y = viewBoxHeight - verticalGap - yStep * (curPoint.value - minVal);
        }        
        diagramSeries.minVal = roundingSettings.roundTo * Math.floor((minVal - verticalGap / yStep) / roundingSettings.roundTo);
        diagramSeries.maxVal = roundingSettings.roundTo * Math.ceil((maxVal + verticalGap / yStep) / roundingSettings.roundTo);

        if (oldControlValPoint) {
            diagramSeries.oldControlValX = convertX(oldControlValPoint.xPos - minXPos + 1.5);
            diagramSeries.oldControlValY = convertY(oldControlValPoint.value);
        }
        if (newControlValPoint) {
            diagramSeries.newControlValX = convertX(newControlValPoint.xPos - minXPos + 1.5);
            diagramSeries.newControlValY = convertY(newControlValPoint.value);
        }

        if (diagramSeries.bandTetragon && !diagramSeries.bandTetragonStr) {
            let tetragon = diagramSeries.bandTetragon;
            diagramSeries.bandTetragonStr = `${convertX(tetragon.leftX+1.5)},${convertY(tetragon.topLeftY)} ${convertX(tetragon.rightX+1.5)},${convertY(tetragon.topRightY)} ${convertX(tetragon.rightX+1.5)},${convertY(tetragon.bottomRightY)} ${convertX(tetragon.leftX+1.5)},${convertY(tetragon.bottomLeftY)}`;
        }
    }

    public static findByTabUId(views: CardsModel[], tabUId: string): [CardsModel, Tab | undefined] {
        for (let i=0; i < views.length; ++i) {
            if (views[i].tabUId == tabUId) {
                return [ views[i], undefined ];
            }
            if (views[i].tabs) {
                for (let j=0; j < views[i].tabs.length; ++j) {
                    if (views[i].tabs[j].tabUId == tabUId) {
                        return [ views[i], views[i].tabs[j] ];
                    }
                }
            }
        }
        return [undefined, undefined ];
    }

    public static cloneObject<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    public static debounce(func: () => void, wait = 100) {
        let h: any;
        return () => {
            clearTimeout(h);
            h = setTimeout(() => func(), wait);
        };
    }

    public static getSelectedViewKey(projectId: string): string {
        return 'selectedView_' + projectId;
    }

    /**
     * An in-app link of a story (a trend link, a view heading of the project story) opened in the story's own project.
     * The links name the tab by its UId, which a project keeps when it is exported and imported. Older stories also
     * carry a projectId and a viewId, the ones of the project the story was generated in: they are replaced.
     */
    public static createStoryLinkUrlTree(router: Router, href: string, projectId: string): UrlTree {
        const urlTree = router.parseUrl(href);
        const queryParams = { ...urlTree.queryParams, projectId: projectId };
        delete queryParams['viewId'];
        urlTree.queryParams = queryParams;
        return urlTree;
    }

    public static findDimensionInLegend(legend: DimensionName[][], dimIndex: number): DimensionName {
        for (let i = 0; i < legend.length; ++i) {
            for (let j = 0; j < legend[i].length; ++j) {
                if (legend[i][j].index == dimIndex) {
                    return legend[i][j];
                }
            }
        }
        return undefined;
    }

    public static findBgColorFromLegend(legend: DimensionName[][], dimIndex: number): string {
        let dim = this.findDimensionInLegend(legend, dimIndex);
        if (dim) {
            return dim.bgColor;
        }
        return '#000000';
    }

    public static loadBgColorToDimensionsFilter(legend: DimensionName[][], dimensionsFilter: DimensionsFilter): void {
        try {
            if (dimensionsFilter && dimensionsFilter.items) {
                for (let i = 0; i < dimensionsFilter.items.length; ++i) {
                    dimensionsFilter.items[i].bgColor = this.findBgColorFromLegend(legend, dimensionsFilter.items[i].dimensionIndex);
                }
            }
        } catch (ex) {
            console.error(ex);
        }
    }

    public static getDimensionsCount(legend: LegendItem[][]): number {
        let maxDimIndex = 0;
        for (let i = 0; i < legend.length; ++i) {
            for (let j = 0; j < legend[i].length; ++j) {
                if (maxDimIndex < legend[i][j].index) {
                    maxDimIndex = legend[i][j].index;
                }
            }
        }
        return maxDimIndex + 1;
    }

    public static getLockedDimensions(legend: LegendItem[][]): DimensionLockType[] {
        let dimCount = this.getDimensionsCount(legend);
        let lockedDimensions = new Array<DimensionLockType>(dimCount);
        for (let i = 0; i < legend.length; ++i) {
            for (let j = 0; j < legend[i].length; ++j) {
                lockedDimensions[legend[i][j].index] = legend[i][j].lockType;
            }
        }
        return lockedDimensions;
    }

    // Minimum depth every driver in the tab/view necessarily has: a column counts when any of its
    // dimensions is fixed by the combo (isInFilter) or locked to a value (matches backend DepthFilter).
    public static calcMinSelectableDepth(legend: LegendItem[][]): number {
        let count = 0;
        for (let i = 0; i < legend.length; ++i) {
            if (legend[i].some(item => item.isInFilter || item.lockType == DimensionLockType.Value)) {
                ++count;
            }
        }
        return count;
    }

    public static calcAllowedMaxDepth(legend: LegendItem[][]): number {
        let res = legend.length;
        for (let i = 0; i < legend.length; ++i) {
            // Combo-fixed (isInFilter) or value-locked columns are forced non-{All} and always count
            // toward depth (see calcMinSelectableDepth / backend DepthFilter); only subtract columns
            // that are incidentally single-value.
            const isForcedNonAll = legend[i].some(item => item.isInFilter || item.lockType == DimensionLockType.Value);
            if (!isForcedNonAll && legend[i][legend[i].length - 1].isSingleValue) {
                --res;
            }
        }
        return res;
    }

    public static initMainFcstWalkModel(model: MainFcstWalkModel): void {
        if (model.tabs?.length) {
            model.tabsTree = FcstWalkTreeBuilder.buildTree(model.tabs);
        }
    }

    public static createLogCard(card: Card) {
        return {
            driverType: card?.driverType,
            formulaComponentIndex: card?.formulaComponentIndex,
            dimensions: card?.dimensions?.map(d => `${d?.index}:${d?.name}`).join(', '),
        };
    }

    public static areCardsEqual(card1: Card, card2: Card): boolean {
        /*console.log('areCardsEqual:', {
            card1: card1 ? { driverName: card1.driverName, driverType: card1.driverType, formulaComponentIndex: card1.formulaComponentIndex, dimensions: card1.dimensions, seqDriverValue: card1.seqDriverValue } : card1,
            card2: card2 ? { driverName: card2.driverName, driverType: card2.driverType, formulaComponentIndex: card2.formulaComponentIndex, dimensions: card2.dimensions, seqDriverValue: card2.seqDriverValue } : card2
        });*/
        if (!card1 || !card2) {
            return card1 === card2;
        }
        if (card1.driverType !== card2.driverType) {
            return false;
        }
        if (card1.formulaComponentIndex !== card2.formulaComponentIndex) {
            return false;
        }
        if (card1.driverName !== card2.driverName) {
            return false;
        }
        // Compare dimensions arrays
        if (!card1.dimensions || !card2.dimensions) {
            return card1.dimensions === card2.dimensions;
        }
        if (card1.dimensions.length !== card2.dimensions.length) {
            return false;
        }
        for (let i = 0; i < card1.dimensions.length; i++) {
            const dim1 = card1.dimensions[i];
            const dim2 = card2.dimensions[i];
            if (!dim1 || !dim2) {
                if (dim1 !== dim2) {
                    return false;
                }
            } else if (dim1.index !== dim2.index) {
                return false;
            }
        }
        return true;
    }

    /**
     * Finds indexes of newly added cards by comparing two card arrays.
     */
    public static findAddedCardIndexes(oldCards: Card[], newCards: Card[]): number[] {
        /*console.log('findAddedCardIndexes:', {
            oldCards: oldCards?.map(c => ({ driverName: c.driverName, dimensions: c.dimensions, seqDriverValue: c.seqDriverValue })),
            newCards: newCards?.map(c => ({ driverName: c.driverName, dimensions: c.dimensions, seqDriverValue: c.seqDriverValue }))
        });*/
        if (!oldCards || oldCards.length === 0) {
            // All cards in newCards are new
            return newCards ? Array.from({ length: newCards.length }, (_, i) => i) : [];
        }
        if (!newCards || newCards.length === 0) {
            return [];
        }

        const addedIndexes: number[] = [];

        for (let newIndex = 0; newIndex < newCards.length; newIndex++) {
            const newCard = newCards[newIndex];
            
            // Check if this card exists anywhere in the old array
            const foundInOld = oldCards.some(oldCard => this.areCardsEqual(newCard, oldCard));

            if (!foundInOld) {
                // This card doesn't exist in the old array - it's a new card
                addedIndexes.push(newIndex);
            }
        }

        return addedIndexes;
    }

    
    public static highlightNewlyAddedCards(oldCards: Card[], newCards: Card[] | undefined): void {
        //console.log('highlightNewlyAddedCards:', oldCards, newCards);
        if (!newCards || newCards.length === 0) {
            return;
        }
        const addedCardIndexes = CardHelper.findAddedCardIndexes(oldCards, newCards);
        //console.log('addedCardIndexes', addedCardIndexes);
        addedCardIndexes.forEach(index => {
            if (index >= 0 && index < newCards.length) {
                newCards[index].isHighlighted = true;
            }
        });
    }

    public static highlightCardsBefore(cards: Card[] | undefined, beforeIndex: number): void {
        if (!cards || cards.length === 0 || beforeIndex <= 0) {
            return;
        }
        const maxIndex = Math.min(beforeIndex, cards.length);
        for (let i = 0; i < maxIndex; i++) {
            cards[i].isHighlighted = true;
        }
    }

    public static highlightCardsAfter(cards: Card[] | undefined, afterIndex: number, balanceCard?: BalanceCard): void {
        if (!cards || cards.length === 0 || afterIndex < 0 || afterIndex >= cards.length) {
            return;
        }
        for (let i = afterIndex; i < cards.length; i++) {
            cards[i].isHighlighted = true;
        }
        if (balanceCard) {
            balanceCard.isHighlighted = true;
        }
    }

    /**
     * Finds the parent tab ID by traversing the hierarchical tab structure.
     * @param hierarchicalTabs - The hierarchical tabs array to search
     * @param targetId - The ID of the tab whose parent we want to find
     * @param parent - The current parent tab (used internally for recursion)
     * @returns The parent tab ID, null if at root level, or undefined if not found
     */
    public static findParentInHierarchy(hierarchicalTabs: Tab[], targetId: string, parent: Tab | null = null): string | null | undefined {
        for (const tab of hierarchicalTabs) {
            if (tab.id === targetId) {
                return parent ? parent.id : null;
            }
            if (tab.children && tab.children.length > 0) {
                const result = CardHelper.findParentInHierarchy(tab.children, targetId, tab);
                if (result !== undefined) {
                    return result;
                }
            }
        }
        return undefined;
    }

}
